/**
 * The retry policy for the two lookups a scanned QR makes:
 * `GET /deeplink/:key` (anonymous, utils/deeplink.js) and
 * `GET /deeplink/:key/resolve` (authenticated, store/modules/courses.js).
 *
 * Both run at a COLD START. A booklet QR read with the phone's camera opens the app
 * from nothing, so these requests race DNS, network bring-up and Auth0's session
 * restore. Both callers answer a failure by sending the member to /home — which is
 * correct for an unknown slug and wrong for a request that never got asked, and the
 * two were indistinguishable. One transient failure was enough to land a member on
 * Home with no spinner, no error and no log, which is what "the QR works sometimes"
 * actually was.
 *
 * Shared rather than duplicated because the two lookups are one user action: a scan
 * that survives the first request only to lose the second has not been made
 * reliable.
 */

/**
 * Long enough to cross a slow mobile handover, short enough that a dead network
 * does not hold the member on a spinner. Without it axios waits indefinitely.
 */
export const LOOKUP_TIMEOUT_MS = 8000;

/**
 * Two retries, back-to-back-ish. The whole budget is ~1.2s of waiting on top of the
 * attempts themselves, which is inside what a QR scan already feels like.
 */
export const LOOKUP_RETRY_DELAYS_MS = [300, 900];

export const waitMs = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Is asking again capable of changing the answer.
 *
 * A response with a status IS an answer: 404 means no such slug and 403 means the
 * member may not have it, and repeating either only delays the same result. A 5xx is
 * the server failing to produce an answer, and no response at all — timeout
 * (ECONNABORTED), DNS failure, offline, CORS — means the question never arrived.
 */
export function isRetriableLookupError(error) {
  const status = error?.response?.status;
  if (typeof status === 'number') return status >= 500;
  return true;
}

/**
 * Run `attemptFn` until it succeeds, the error is a real answer, or the delays run
 * out. Rethrows the LAST error so the caller still sees a genuine 404 as a 404.
 *
 * `onRetry` exists so each call site can name itself in the log. A retry that leaves
 * no trace is how this class of bug stays invisible.
 */
export async function withLookupRetry(attemptFn, { onRetry } = {}) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await attemptFn();
    } catch (error) {
      const delay = LOOKUP_RETRY_DELAYS_MS[attempt];
      if (delay === undefined || !isRetriableLookupError(error)) {
        throw error;
      }
      onRetry?.(error, attempt, delay);
      await waitMs(delay);
    }
  }
}
